/**
 * CleanFriend search drawer – opens from header search icon,
 * shows popular chips when empty and predictive product cards when typing.
 */
class CfSearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.activeElement = null;
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.panel = this.querySelector('.cf-search-drawer__panel');
    this.input = this.querySelector('[data-cf-search-input]');
    this.clearBtn = this.querySelector('[data-cf-search-clear]');
    this.form = this.querySelector('[data-cf-search-form]');
    this.popular = this.querySelector('[data-cf-search-popular]');
    this.results = this.querySelector('[data-cf-search-results]');
    this.loading = this.querySelector('[data-cf-search-loading]');
    this.empty = this.querySelector('[data-cf-search-empty]');

    this.onKeyup = this.onKeyup.bind(this);
    this.onOpenClick = this.onOpenClick.bind(this);
    this.onInput = this.debounce(this.onInput.bind(this), 280);

    document.addEventListener('click', this.onOpenClick);

    this.querySelectorAll('[data-cf-search-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.querySelectorAll('[data-cf-search-term]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const term = chip.getAttribute('data-cf-search-term') || '';
        this.input.value = term;
        this.toggleClear();
        this.input.focus();
        this.onInput();
      });
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.searchTerm = '';
        this.toggleClear();
        this.showPopular();
        this.input.focus();
      });
    }

    if (this.input) {
      this.input.addEventListener('input', this.onInput);
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) event.preventDefault();
      });
    }

    this.results?.addEventListener('click', this.onResultsClick.bind(this));

    if (this.empty?.dataset.template) {
      this.setAttribute('data-empty-template', this.empty.dataset.template);
    }
  }

  disconnectedCallback() {
    if (this.onOpenClick) document.removeEventListener('click', this.onOpenClick);
    if (this.onKeyup) document.removeEventListener('keyup', this.onKeyup);
  }

  onOpenClick(event) {
    const btn = event.target.closest('[data-cf-search-open]');
    if (!btn) return;
    event.preventDefault();
    this.open(btn);
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.classList.contains('active') && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    }

    this.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('is-open');
    });

    document.body.classList.add('overflow-hidden');
    document.querySelectorAll('[data-cf-search-open]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'true');
    });

    document.addEventListener('keyup', this.onKeyup);

    setTimeout(() => {
      this.input?.focus();
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.input);
    }, 50);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    document.querySelectorAll('[data-cf-search-open]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.removeEventListener('keyup', this.onKeyup);

    const finish = () => {
      this.hidden = true;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    };

    const panel = this.panel;
    if (!panel) {
      finish();
      return;
    }

    const onEnd = (event) => {
      if (event.target !== panel) return;
      panel.removeEventListener('transitionend', onEnd);
      finish();
    };
    panel.addEventListener('transitionend', onEnd);
    setTimeout(finish, 400);
  }

  onKeyup(event) {
    if (event.code === 'Escape') this.close();
  }

  toggleClear() {
    if (!this.clearBtn) return;
    this.clearBtn.classList.toggle('hidden', !this.input.value.length);
  }

  onInput() {
    this.toggleClear();
    const term = (this.input?.value || '').trim();

    if (!term.length) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showPopular();
      return;
    }

    if (term === this.searchTerm) return;
    this.searchTerm = term;
    this.getResults(term);
  }

  showPopular() {
    this.popular?.removeAttribute('hidden');
    this.results?.setAttribute('hidden', '');
    this.loading?.setAttribute('hidden', '');
    this.empty?.setAttribute('hidden', '');
    this.results && (this.results.innerHTML = '');
    this.input?.setAttribute('aria-expanded', 'false');
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.popular?.setAttribute('hidden', '');
      this.results?.setAttribute('hidden', '');
      this.empty?.setAttribute('hidden', '');
      this.loading?.removeAttribute('hidden');
    } else {
      this.loading?.setAttribute('hidden', '');
    }
  }

  getResults(term) {
    const key = term.toLowerCase();
    this.setLoading(true);

    if (this.cachedResults[key]) {
      this.renderResults(this.cachedResults[key], term);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const url =
      (window.routes?.predictive_search_url || '/search/suggest') +
      `?q=${encodeURIComponent(term)}` +
      '&resources[type]=product' +
      '&resources[limit]=8' +
      '&section_id=cf-search-drawer-results';

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-cf-search-drawer-results');
        const markup = html ? html.innerHTML : '';
        this.cachedResults[key] = markup;
        this.renderResults(markup, term);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.setLoading(false);
        this.showEmpty(term);
      });
  }

  renderResults(markup, term) {
    this.setLoading(false);
    this.popular?.setAttribute('hidden', '');

    if (!markup || !markup.trim() || markup.includes('data-cf-sd-empty')) {
      this.showEmpty(term);
      return;
    }

    this.empty?.setAttribute('hidden', '');
    this.results.innerHTML = markup;
    this.results.removeAttribute('hidden');
    this.input?.setAttribute('aria-expanded', 'true');
  }

  showEmpty(term) {
    this.results?.setAttribute('hidden', '');
    this.popular?.setAttribute('hidden', '');
    if (this.empty) {
      const template = this.getAttribute('data-empty-template') || '';
      this.empty.textContent = template
        ? template.replace('__TERM__', term)
        : `Der blev ikke fundet nogen resultater for "${term}".`;
      this.empty.removeAttribute('hidden');
    }
    this.input?.setAttribute('aria-expanded', 'false');
  }

  async onResultsClick(event) {
    const btn = event.target.closest('[data-cf-sd-atc]');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(window.routes?.cart_add_url || '/cart/add', config);
      const data = await response.json();
      if (data.status) throw new Error(data.description || 'Add failed');

      this.close();

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (err) {
      console.error(err);
      window.location.href = btn.closest('a')?.href || window.routes?.cart_url || '/cart';
    } finally {
      btn.disabled = false;
    }
  }
}

customElements.define('cf-search-drawer', CfSearchDrawer);
